import { useEffect, useState, useRef, useMemo } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSession, useUser } from "@clerk/clerk-react";
import { Spinner, Tooltip, Button } from "@heroui/react";
import { Repeat, Sliders } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { DashboardPreferencesManager, DashboardBlock } from "./utils/dashboardPreferences";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { SortableBlock, DashboardCustomizer } from "./componentsNew";

// Import all block components
import {
  MetricsBlockNew,
  HelpdeskMetricsBlockNew,
  VacancyMetricsBlockNew,
  SummaryMetricsBlockNew,
  HelpdeskTicketsBlockNew,
  CandidatesBlockNew,
  TasksBlockNew,
  SlackFeedBlockNew,
  DocumentsBlockNew,
  EPCMetricsBlockNew,
  RealEstateMetricsBlockNew,
  RenovationPhotosBlockNew,
  CompanyPortfolioBlockNew,
  AppointmentsTodayBlockNew,
  ContractualRentBlockNew,
} from "./componentsNew";

const HomeNew = () => {
  const { session } = useSession();
  const { user } = useUser();
  
  // State management
  const [keyFigures, setKeyFigures] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");
  const [helpdeskLoading, setHelpdeskLoading] = useState(true);
  const [vacancyLoading, setVacancyLoading] = useState(true);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [visibleBlocks, setVisibleBlocks] = useState<DashboardBlock[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Track if data has been loaded to prevent unnecessary re-fetches
  const hasLoadedData = useRef(false);

  // Helpdesk stats
  const [weeklyCompletedTasksByUser, setWeeklyCompletedTasksByUser] = useState<number>(0);
  const [weeklyCompletedTasksByEveryone, setWeeklyCompletedTasksByEveryone] = useState<number>(0);
  const [openTicketsCount, setOpenTicketsCount] = useState<{ total: number; open: number; completion: number } | null>(null);
  const [completedTicketsStats, setCompletedTicketsStats] = useState({
    thisWeek: {
      totalNew: 0,
      totalCompleted: 0,
      newTickets: [],
      completedTickets: [],
      assignments: {
        withLabels: 0,
        withResponsible: 0,
        withSupplier: 0,
        withSource: 0,
        total: 0
      }
    },
    lastWeek: {
      totalNew: 0,
      totalCompleted: 0,
      newTickets: [],
      completedTickets: [],
      assignments: {
        withLabels: 0,
        withResponsible: 0,
        withSupplier: 0,
        withSource: 0,
        total: 0
      }
    },
    performance: {
      status: 'on-track',
      totalNewPast2Weeks: 0,
      totalCompletedPast2Weeks: 0,
      completionRate: 100
    },
    serviceLevel: {
      tenantTicketsThisMonth: 0,
      openTenantTicketsThisMonth: 0,
      servicePercentage: 100
    }
  });

  // Vacancy details
  const [vacancyDetails, setVacancyDetails] = useState<{
    units: Array<any>;
    totalLostRevenue: number;
  }>({ units: [], totalLostRevenue: 0 });

  // Load dashboard preferences
  useEffect(() => {
    setVisibleBlocks(DashboardPreferencesManager.getVisibleBlocks());
  }, []);

  const refreshPreferences = () => {
    setVisibleBlocks(DashboardPreferencesManager.getVisibleBlocks());
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = visibleBlocks.findIndex((block) => block.id === active.id);
      const newIndex = visibleBlocks.findIndex((block) => block.id === over.id);

      const newBlocks = [...visibleBlocks];
      const [movedBlock] = newBlocks.splice(oldIndex, 1);
      newBlocks.splice(newIndex, 0, movedBlock);

      const updatedBlocks = newBlocks.map((block, index) => ({
        ...block,
        order: index,
      }));

      const allBlocks = DashboardPreferencesManager.getPreferences().map((block) => {
        const updatedBlock = updatedBlocks.find((b) => b.id === block.id);
        return updatedBlock || block;
      });

      DashboardPreferencesManager.savePreferences(allBlocks);
      setVisibleBlocks(updatedBlocks);
    }
    
    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Calculated values
  const formattedOPEXValue = useMemo(() => {
    const nonChargeableOpex = Number(keyFigures?.nonChargeableOpexCosts ?? 0);
    const invoicedRentLines = Number(keyFigures?.invoicedRentLines ?? 0);

    if (!invoicedRentLines) {
      return '0.0';
    }

    const ratio = Math.abs((nonChargeableOpex / invoicedRentLines) * 100);
    return Number.isFinite(ratio) ? ratio.toFixed(1) : '0.0';
  }, [keyFigures]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/key-figures/refresh`,
        { method: "GET" },
        session,
      );
      const { data, lastUpdated } = await res.json();
      setKeyFigures(data);
      setLastUpdated(lastUpdated);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Vacancy data fetcher — extracted so it can also be called as a refetch callback
  const fetchVacancyDetails = async () => {
    setVacancyLoading(true);
    try {
      const [vacantResponse, upcomingResponse] = await Promise.all([
        fetchWithAuth(`${API_URL}/rental/metrics/enhanced-vacant-units`, { method: "GET" }, session),
        fetchWithAuth(`${API_URL}/rental/metrics/enhanced-upcoming-vacant-units`, { method: "GET" }, session)
      ]);

      const vacantUnits = vacantResponse.ok ? await vacantResponse.json() : [];
      const upcomingUnits = upcomingResponse.ok ? await upcomingResponse.json() : [];

      // Process units with full calculation logic
      const processUnits = (units: any[], status: 'current' | 'upcoming') => {
        return units.map((unit: any) => {
          const today = new Date();
          let vacantSince: Date | undefined;
          let contractEndDate: Date | undefined;
          let daysVacant = 0;
          let lostRevenue = 0;
          let urgencyLevel: 'low' | 'medium' | 'high' = 'low';
          let lastTenantNames: string = '';
          let vacancyReason: 'contract_end' | 'since_acquisition' = 'contract_end';

          // Look for the most recent contract end date and get tenant names
          const allContractsWithEndDates: Array<{
            contract: any;
            endDate: Date;
          }> = [];

          // Check if unit has contracts with milestone dates
          if (unit.contracts && unit.contracts.length > 0) {
            unit.contracts.forEach((contract: any) => {
              if (contract.contractMilestoneDates && contract.contractMilestoneDates.length > 0) {
                const contractEndDates = contract.contractMilestoneDates
                  .filter((milestone: any) => milestone.milestoneDateType === 'Einddatum' || milestone.milestoneDateType === 'END_DATE')
                  .map((milestone: any) => new Date(milestone.date))
                  .filter((date: Date) => {
                    // For closed contracts, ignore future end dates (stale milestones from original term)
                    if (contract.phase === 'Gesloten') return date <= today;
                    return true;
                  });

                if (contractEndDates.length > 0) {
                  const mostRecentEndDate = contractEndDates.sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];
                  allContractsWithEndDates.push({
                    contract,
                    endDate: mostRecentEndDate
                  });
                }
              }
            });
          }

          // Fallback: Check if unit directly has milestone dates
          if (allContractsWithEndDates.length === 0 && unit.contractMilestoneDates && unit.contractMilestoneDates.length > 0) {
            const unitEndDates = unit.contractMilestoneDates
              .filter((milestone: any) => milestone.milestoneDateType === 'Einddatum' || milestone.milestoneDateType === 'END_DATE')
              .map((milestone: any) => new Date(milestone.date));

            if (unitEndDates.length > 0) {
              contractEndDate = unitEndDates.sort((a: Date, b: Date) => b.getTime() - a.getTime())[0];
            }
          } else if (allContractsWithEndDates.length > 0) {
            const mostRecentContractData = allContractsWithEndDates.sort((a, b) => b.endDate.getTime() - a.endDate.getTime())[0];
            contractEndDate = mostRecentContractData.endDate;

            // Extract tenant names from the most recent contract
            if (mostRecentContractData.contract.contractContacts) {
              const tenantNames = mostRecentContractData.contract.contractContacts
                .filter((contact: any) => contact.contactType === 'Contractant (huurder)')
                .map((contact: any) => contact.resource?.name)
                .filter((name: string) => name)
                .join(', ');

              if (tenantNames) {
                lastTenantNames = tenantNames;
              }
            }
          }

          // If no contract end date found, fall back to unitBuiltAt (delivery date) or building acquisition date
          if (!contractEndDate && (unit.unitBuiltAt || unit.building?.acquisitionDate)) {
            vacantSince = unit.unitBuiltAt
              ? new Date(unit.unitBuiltAt)
              : new Date(unit.building.acquisitionDate);
            vacancyReason = 'since_acquisition';
          } else if (contractEndDate) {
            vacantSince = contractEndDate;
          }

          // For current vacant units: calculate based on vacancy start date vs today
          if (status === 'current' && vacantSince && vacantSince <= today) {
            daysVacant = Math.floor((today.getTime() - vacantSince.getTime()) / (1000 * 60 * 60 * 24));

            // Calculate lost revenue: ERV * (days vacant / 30.44 average days per month)
            const monthsVacant = daysVacant / 30.44;
            lostRevenue = Math.round((unit.estimatedRentalValue || 0) * monthsVacant);

            // Determine urgency level
            if (daysVacant > 90) urgencyLevel = 'high';
            else if (daysVacant > 30) urgencyLevel = 'medium';
            else urgencyLevel = 'low';
          }
          // For upcoming vacant units: store the future end date
          else if (status === 'upcoming' && contractEndDate && contractEndDate > today) {
            vacantSince = contractEndDate;
            daysVacant = 0;
            lostRevenue = 0;
          }

          return {
            id: unit.id,
            unitNumber: unit.unitNumber || unit.id,
            building: unit.building?.displayName || unit.building?.id || 'Onbekend',
            erv: unit.estimatedRentalValue || 0,
            vacantSince,
            contractEndDate,
            daysVacant,
            lostRevenue,
            status,
            urgencyLevel,
            lastTenantNames,
            vacancyReason,
            unitBuiltAt: unit.unitBuiltAt ? new Date(unit.unitBuiltAt) : undefined,
            buildingAcquisitionDate: unit.building?.acquisitionDate ? new Date(unit.building.acquisitionDate) : undefined,
            immowebReferenceId: unit.immowebReferenceId,
            immowebReferenceIdAddedTimestamp: unit.immowebReferenceIdAddedTimestamp,
            visibleInCalendar: unit.visibleInCalendar,
            visitationResponsibleContact: unit.visitationResponsibleContact,
            applicationStats: unit.applicationStats,
            availableTimeslots: unit.availableTimeslots,
            timeslotDetails: unit.timeslotDetails,
            calculationDetails: {
              ervPerMonth: unit.estimatedRentalValue || 0,
              daysVacant,
              monthsVacant: daysVacant > 0 ? (daysVacant / 30.44) : 0,
              formula: `€${unit.estimatedRentalValue || 0}/maand × ${daysVacant > 0 ? (daysVacant / 30.44).toFixed(2) : 0} maanden = €${lostRevenue}`
            },
            contracts: unit.contracts || [],
          };
        });
      };

      const currentVacantUnits = processUnits(vacantUnits, 'current');
      const upcomingVacantUnits = processUnits(upcomingUnits, 'upcoming');

      const allUnits = [...currentVacantUnits, ...upcomingVacantUnits];
      const totalLostRevenue = currentVacantUnits.reduce((total: number, unit: any) => total + (unit.lostRevenue || 0), 0);

      setVacancyDetails({
        units: allUnits,
        totalLostRevenue
      });

    } catch (error) {
      console.error("Error fetching vacancy details:", error);
    } finally {
      setVacancyLoading(false);
    }
  };

  // Data fetching
  useEffect(() => {
    // Only fetch if we have a session and haven't loaded data yet
    if (!session || hasLoadedData.current) {
      return;
    }

    async function fetchKeyFigures() {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/key-figures`,
          { method: "GET" },
          session,
        );
        const { data, lastUpdated } = await response.json();
        setKeyFigures(data);
        setLastUpdated(lastUpdated);
      } catch (error) {
        console.error("Error fetching key figures:", error);
      }
    }

    async function getWeeklyCompletedTasks() {
      if (!user?.id) return;
      
      try {
        const response = await fetchWithAuth(
          `${API_URL}/tasks/weekly-tasks/${user.id}`,
          { method: "GET" },
          session,
        );
        const { userCompleted, totalCompleted } = await response.json();
        setWeeklyCompletedTasksByUser(userCompleted);
        setWeeklyCompletedTasksByEveryone(totalCompleted);
      } catch (error) {
        console.error("Error fetching weekly tasks:", error);
      }
    }

    async function fetchOpenTicketsCount() {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/tickets/total-count`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setOpenTicketsCount({
          total: data.totalCount,
          open: data.openCount,
          completion: data.completionCount
        });
      } catch (error) {
        console.error("Error fetching open tickets count:", error);
      }
    }

    async function fetchCompletedTicketsStats() {
      setHelpdeskLoading(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/completed-stats`,
          { method: "GET" },
          session,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data && data.thisWeek && data.lastWeek) {
          setCompletedTicketsStats({
            thisWeek: data.thisWeek,
            lastWeek: data.lastWeek,
            performance: data.performance || completedTicketsStats.performance,
            serviceLevel: data.serviceLevel || completedTicketsStats.serviceLevel
          });
        }
      } catch (error) {
        console.error("Error fetching completed tickets stats:", error);
      } finally {
        setHelpdeskLoading(false);
      }
    }

    // Fetch all data
    async function loadAllData() {
      await Promise.all([
        fetchKeyFigures(),
        getWeeklyCompletedTasks(),
        fetchOpenTicketsCount(),
        fetchCompletedTicketsStats(),
        fetchVacancyDetails()
      ]);

      // Mark as loaded to prevent re-fetches on tab switch
      hasLoadedData.current = true;
    }

    loadAllData();
  }, [session?.id, user?.id]); // Only depend on stable IDs, not the entire objects

  // Block rendering map
  const blockComponents: Record<string, JSX.Element> = {
    'metrics-overview': (
      <MetricsBlockNew
        keyFigures={keyFigures}
        formattedOPEXValue={formattedOPEXValue}
        weeklyCompletedTasksByUser={weeklyCompletedTasksByUser}
        weeklyCompletedTasksByEveryone={weeklyCompletedTasksByEveryone}
        openTicketsCount={openTicketsCount}
      />
    ),
    'helpdesk-metrics': (
      <HelpdeskMetricsBlockNew isLoading={helpdeskLoading} />
    ),
    'vacancy-metrics': (
      <VacancyMetricsBlockNew
        keyFigures={keyFigures}
        vacancyDetails={vacancyDetails}
        isLoading={vacancyLoading}
        onRefresh={fetchVacancyDetails}
      />
    ),
    'summary-metrics': (
      <SummaryMetricsBlockNew
        keyFigures={keyFigures}
        formattedOPEXValue={formattedOPEXValue}
        weeklyCompletedTasksByUser={weeklyCompletedTasksByUser}
        weeklyCompletedTasksByEveryone={weeklyCompletedTasksByEveryone}
      />
    ),
    'helpdesk-tickets': <HelpdeskTicketsBlockNew />,
    'candidates': <CandidatesBlockNew />,
    'tasks': <TasksBlockNew />,
    'slack-feed': <SlackFeedBlockNew />,
    'documents': (
      <DocumentsBlockNew
        sharePointFolderId="01V4TSQ4LKM3Y5OKHCUZEYIK3GCCULDZMT"
        sharePointFolderUrl="https://xyzinvestbv.sharepoint.com/sites/XYZinvest/Gedeelde%20documenten/23.%20Yallyhub/Documenten%20dashboard"
      />
    ),
    'epc-metrics': <EPCMetricsBlockNew />,
    'real-estate-metrics': <RealEstateMetricsBlockNew />,
    'renovation-photos': <RenovationPhotosBlockNew />,
    'company-portfolio': <CompanyPortfolioBlockNew />,
    'appointments-today': <AppointmentsTodayBlockNew />,
    'contractual-rent': <ContractualRentBlockNew />,
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Dashboard &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      
      <div className="z-10 pt-6 mb-10">
        {/* Header with data refresh and customization */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Welkom terug, {user?.firstName}!
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Data refresh */}
            <div className="flex items-center gap-2 text-sm text-neutral-secondary-mid">
              {loading ? (
                <>
                  <Spinner size="sm" color="success" />
                  <span>Data aan het ophalen...</span>
                </>
              ) : (
                lastUpdated && (
                  <p className="text-xs">
                    Laatst bijgewerkt: <b>{lastUpdated}</b>
                  </p>
                )
              )}
              <Tooltip content="Data refreshen">
                <button
                  onClick={handleRefresh}
                  className="p-2 rounded-lg hover:bg-neutral-primary/5 transition-colors"
                  disabled={loading}
                >
                  <Repeat
                    weight="duotone"
                    size={20}
                    className="text-moss-primary"
                  />
                </button>
              </Tooltip>
            </div>

            {/* Dashboard customizer */}
            <Tooltip content="Dashboard aanpassen">
              <Button
                isIconOnly
                variant="flat"
                onPress={() => setShowCustomizer(true)}
                className="bg-moss-primary/10 hover:bg-moss-primary/20"
              >
                <Sliders size={20} weight="duotone" className="text-moss-primary" />
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Metrics Overview - Full Width (if visible) */}
        {visibleBlocks.some((block) => block.id === 'metrics-overview') && (
          <div className="mb-6 lg:mb-8">
            {blockComponents['metrics-overview']}
          </div>
        )}

        {/* Dashboard with drag-and-drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={visibleBlocks.filter((block) => block.id !== 'metrics-overview').map((block) => block.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid gap-6 lg:gap-8 lg:grid-cols-3">
              {visibleBlocks
                .filter((block) => block.id !== 'metrics-overview')
                .map((block) => (
                  <SortableBlock key={block.id} id={block.id}>
                    {blockComponents[block.id]}
                  </SortableBlock>
                ))}
            </div>
          </SortableContext>
          
          <DragOverlay>
            {activeId ? (
              <div className="opacity-90 shadow-2xl rounded-lg ring-2 ring-moss-primary">
                {blockComponents[activeId]}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Empty state if no blocks are visible */}
        {visibleBlocks.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-neutral-secondary-mid">
            <Sliders size={64} weight="duotone" className="mb-4 text-neutral-secondary-mid/50" />
            <h3 className="text-xl font-semibold mb-2">Geen blokken zichtbaar</h3>
            <p className="text-sm mb-4">Klik op de instellingen knop om blokken toe te voegen aan je dashboard</p>
            <Button
              color="primary"
              startContent={<Sliders size={18} weight="duotone" />}
              onPress={() => setShowCustomizer(true)}
            >
              Dashboard aanpassen
            </Button>
          </div>
        )}
      </div>

      {/* Dashboard Customizer Modal */}
      <DashboardCustomizer
        isOpen={showCustomizer}
        onClose={() => setShowCustomizer(false)}
        onPreferencesChange={refreshPreferences}
      />
    </>
  );
};

export default HomeNew;

